/**
 * Ecowitt Live Sensor Tile (dashboard)
 * ====================================
 *
 * Surfaces all connected Ecowitt soil sensors — moisture (WH51) and
 * temperature (WN34) — on the dashboard. Auto-refreshes every 60s so a
 * homeowner can bury one probe at a time and watch the channel "light up"
 * (drop in temp, jump in moisture) to identify which channel is where.
 *
 * States:
 *   1. Not configured   → renders null (no UI noise for free users)
 *   2. Configured, loading → skeleton
 *   3. Live              → 6-channel grid + outdoor weather strip
 *   4. Upstream error    → muted error + retry link to /profile
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Drop, Thermometer, ArrowRight } from '@phosphor-icons/react';

import { API_URL } from '../config/api';

const REFRESH_MS = 60 * 1000;  // 1 min — fast enough to see a probe drop in temp post-burial

const BAND_STYLE = {
  dry:       { bg: 'bg-rose-50 dark:bg-rose-950/30',     border: 'border-rose-200 dark:border-rose-900', text: 'text-rose-900 dark:text-rose-200' },
  low:       { bg: 'bg-amber-50 dark:bg-amber-950/30',   border: 'border-amber-200 dark:border-amber-900', text: 'text-amber-900 dark:text-amber-200' },
  optimal:   { bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-200 dark:border-emerald-900', text: 'text-emerald-900 dark:text-emerald-200' },
  wet:       { bg: 'bg-sky-50 dark:bg-sky-950/30',       border: 'border-sky-200 dark:border-sky-900', text: 'text-sky-900 dark:text-sky-200' },
  saturated: { bg: 'bg-indigo-50 dark:bg-indigo-950/30', border: 'border-indigo-200 dark:border-indigo-900', text: 'text-indigo-900 dark:text-indigo-200' },
};

// Soil EC bands (μS/cm) — matches backend parser thresholds
const EC_STYLE = {
  deficient: { bg: 'bg-amber-50 dark:bg-amber-950/30',     border: 'border-amber-200 dark:border-amber-900', text: 'text-amber-900 dark:text-amber-200', label: 'deficient' },
  ideal:     { bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-200 dark:border-emerald-900', text: 'text-emerald-900 dark:text-emerald-200', label: 'ideal' },
  watch:     { bg: 'bg-orange-50 dark:bg-orange-950/30',   border: 'border-orange-200 dark:border-orange-900', text: 'text-orange-900 dark:text-orange-200', label: 'watch' },
  high:      { bg: 'bg-rose-50 dark:bg-rose-950/30',       border: 'border-rose-200 dark:border-rose-900', text: 'text-rose-900 dark:text-rose-200', label: 'high · flush' },
};

function moistureBand(pct) {
  if (pct == null) return 'optimal';
  if (pct < 15) return 'dry';
  if (pct < 30) return 'low';
  if (pct < 55) return 'optimal';
  if (pct < 70) return 'wet';
  return 'saturated';
}

function ageString(epochSec) {
  if (!epochSec) return '';
  const ageS = Math.max(0, Math.floor(Date.now() / 1000 - parseInt(epochSec, 10)));
  if (ageS < 60) return `${ageS}s ago`;
  if (ageS < 3600) return `${Math.floor(ageS / 60)}m ago`;
  return `${Math.floor(ageS / 3600)}h ago`;
}

// ─── Per-channel chip ────────────────────────────────────────
function MoistureChip({ ch }) {
  const band = moistureBand(ch.moisture_pct);
  const style = BAND_STYLE[band];
  const inSoil = (ch.moisture_pct || 0) >= 5;  // >5% = probably buried
  const isWh52 = ch.sensor_model === 'WH52';
  return (
    <div
      className={`flex-1 min-w-[100px] rounded-lg border ${style.border} ${style.bg} p-3`}
      data-testid={`ecowitt-soil-ch-${ch.channel}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`text-[10px] uppercase tracking-widest font-bold ${style.text}`}>
          CH{ch.channel}{isWh52 && <span className="ml-1 px-1 rounded bg-emerald-600/20 text-[8px] tracking-wider align-middle" data-testid={`ecowitt-wh52-badge-ch${ch.channel}`}>WH52</span>}
        </span>
        <span className={`text-[9px] uppercase ${style.text} opacity-60`}>
          {inSoil ? 'in-soil' : 'air'}
        </span>
      </div>
      <div className={`text-2xl font-bold tabular-nums ${style.text}`}>
        {ch.moisture_pct != null ? `${ch.moisture_pct}%` : '—'}
      </div>
      <div className={`text-[10px] ${style.text} opacity-70 tabular-nums`}>
        ad {ch.ad ?? '—'} · {band}
      </div>
    </div>
  );
}

// ─── Soil EC chip (WH52 only) ────────────────────────────────
function ECChip({ ch }) {
  const band = ch.ec_band || 'ideal';
  const style = EC_STYLE[band] || EC_STYLE.ideal;
  return (
    <div
      className={`flex-1 min-w-[100px] rounded-lg border ${style.border} ${style.bg} p-3`}
      data-testid={`ecowitt-ec-ch-${ch.channel}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`text-[10px] uppercase tracking-widest font-bold ${style.text}`}>
          EC-CH{ch.channel}
        </span>
        <span className={`text-[9px] uppercase ${style.text} opacity-60`}>
          {style.label}
        </span>
      </div>
      <div className={`text-2xl font-bold tabular-nums ${style.text}`}>
        {ch.ec_us_cm != null ? Math.round(ch.ec_us_cm) : '—'}
        <span className="text-xs opacity-70 ml-1">μS/cm</span>
      </div>
      <div className={`text-[10px] ${style.text} opacity-70`}>
        soil salts
      </div>
    </div>
  );
}

function TempChip({ ch }) {
  const t = ch.temp_f;
  // Soil temp band — agronomic, not arbitrary
  const cool = t != null && t < 55;
  const pre = t != null && t >= 50 && t < 65;       // PRE timing band
  const warm = t != null && t >= 65;
  const accent = cool ? 'text-sky-900 dark:text-sky-200' :
                 pre ? 'text-amber-900 dark:text-amber-200' :
                 warm ? 'text-orange-900 dark:text-orange-200' :
                 'text-foreground';
  const bg = cool ? 'bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-900' :
             pre ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900' :
             warm ? 'bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-900' :
             'bg-card border-border';
  return (
    <div className={`flex-1 min-w-[100px] rounded-lg border ${bg} p-3`} data-testid={`ecowitt-temp-ch-${ch.channel}`}>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-[10px] uppercase tracking-widest font-bold ${accent}`}>
          T-CH{ch.channel}
        </span>
        <span className={`text-[9px] uppercase ${accent} opacity-60`}>
          {pre ? 'pre band' : warm ? '>65°F' : cool ? 'cold' : '—'}
        </span>
      </div>
      <div className={`text-2xl font-bold tabular-nums ${accent}`}>
        {t != null ? `${t}°` : '—'}<span className="text-sm opacity-70">F</span>
      </div>
      <div className={`text-[10px] ${accent} opacity-70`}>
        4" depth
      </div>
    </div>
  );
}

// ─── Main tile ───────────────────────────────────────────────
export const EcowittMoistureTile = () => {
  const navigate = useNavigate();
  const [configured, setConfigured] = useState(null);
  const [live, setLive] = useState(null);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);
  const cancelledRef = useRef(false);

  const fetchLive = useCallback(async () => {
    try {
      const l = await axios.get(`${API_URL}/api/ecowitt/live`, { withCredentials: true });
      if (cancelledRef.current) return;
      setLive(l.data);
      setErr(null);
    } catch (e) {
      if (cancelledRef.current) return;
      setErr(e?.response?.data?.detail || 'ecowitt offline');
    }
  }, []);

  // Initial config check + first fetch
  useEffect(() => {
    cancelledRef.current = false;
    (async () => {
      try {
        const c = await axios.get(`${API_URL}/api/ecowitt/credentials`, { withCredentials: true });
        if (cancelledRef.current) return;
        const isOn = !!c.data?.configured;
        setConfigured(isOn);
        if (isOn) await fetchLive();
      } catch {
        if (!cancelledRef.current) setErr('credentials check failed');
      }
    })();
    return () => { cancelledRef.current = true; };
  }, [fetchLive]);

  // Auto-refresh every 60s when configured
  useEffect(() => {
    if (!configured) return undefined;
    const id = setInterval(() => {
      fetchLive();
      setTick((t) => t + 1);
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [configured, fetchLive]);

  // Hide entirely when the user hasn't configured Ecowitt — no empty CTA noise.
  if (configured === null) return null;
  if (configured === false) return null;

  if (err && !live) {
    return (
      <button
        onClick={() => navigate('/profile')}
        className="w-full text-left rounded-xl border border-border bg-card hover:bg-muted/50 px-4 py-3 flex items-center justify-between"
        data-testid="ecowitt-tile-err"
      >
        <div className="flex items-center gap-3">
          <Drop size={18} className="text-muted-foreground" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Ecowitt Sensors</div>
            <div className="text-sm font-semibold text-foreground">Not responding</div>
            <div className="text-xs text-muted-foreground">{err} — tap to reconnect</div>
          </div>
        </div>
        <ArrowRight size={16} className="text-muted-foreground" />
      </button>
    );
  }

  if (!live) {
    return (
      <div className="rounded-xl border border-border bg-card/60 p-4 h-32 animate-pulse" data-testid="ecowitt-tile-loading" />
    );
  }

  const moistureChannels = live.soil_channels || [];
  const tempChannels = live.soil_temp_channels || [];
  const ecChannels = live.soil_ec_channels || [];
  const weather = live.weather || {};
  const totalChans = moistureChannels.length + tempChannels.length;
  const latestTime = moistureChannels[0]?.moisture_time || tempChannels[0]?.temp_time;

  return (
    <div
      className="rounded-xl border border-border bg-card overflow-hidden"
      data-testid="ecowitt-tile-live"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <Drop size={14} weight="fill" className="text-emerald-600" />
          <span className="text-[11px] uppercase tracking-widest font-bold text-foreground">
            Ecowitt Live · {totalChans} sensors
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-muted-foreground tabular-nums">
            {ageString(latestTime)} · auto-refresh 60s
          </span>
          <button
            onClick={() => fetchLive()}
            className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 hover:underline"
            data-testid="ecowitt-tile-refresh-btn"
          >
            refresh
          </button>
        </div>
      </div>

      {/* Moisture row */}
      {moistureChannels.length > 0 && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <Drop size={12} weight="fill" className="text-sky-500" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Soil Moisture (WH51)
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {moistureChannels.map((c) => (
              <MoistureChip key={`m${c.channel}`} ch={c} />
            ))}
          </div>
        </div>
      )}

      {/* Temp row */}
      {tempChannels.length > 0 && (
        <div className="px-4 py-3 border-t border-border">
          <div className="flex items-center gap-2 mb-2">
            <Thermometer size={12} weight="fill" className="text-orange-500" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Soil Temperature (WN34 · WH52)
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {tempChannels.map((c) => (
              <TempChip key={`t${c.channel}`} ch={c} />
            ))}
          </div>
        </div>
      )}

      {/* Soil EC row (WH52 3-in-1 probes only — auto-hides if no EC data) */}
      {ecChannels.length > 0 && (
        <div className="px-4 py-3 border-t border-border" data-testid="ecowitt-ec-row">
          <div className="flex items-center gap-2 mb-2">
            <Drop size={12} weight="fill" className="text-emerald-500" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Soil EC (WH52 · μS/cm)
            </span>
            <span className="text-[9px] text-muted-foreground/70 ml-1">
              ideal 200–1200 · feeds nutrient call
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {ecChannels.map((c) => (
              <ECChip key={`ec${c.channel}`} ch={c} />
            ))}
          </div>
        </div>
      )}

      {/* Outdoor weather strip */}
      <div className="px-4 py-2.5 border-t border-border bg-muted/30 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <span>🌡️ <span className="tabular-nums text-foreground font-semibold">{weather.temp_f != null ? `${weather.temp_f}°F` : '—'}</span></span>
        <span>💧 RH <span className="tabular-nums text-foreground">{weather.rh_pct != null ? `${weather.rh_pct}%` : '—'}</span></span>
        <span>🌬 <span className="tabular-nums text-foreground">{weather.wind_mph != null ? `${weather.wind_mph} mph` : '—'}</span></span>
        <span>☔ 24h <span className="tabular-nums text-foreground">{weather.rain_24h_in != null ? `${weather.rain_24h_in}"` : '—'}</span></span>
        <span className="ml-auto">
          <button
            onClick={() => navigate('/profile')}
            className="underline hover:text-foreground"
            data-testid="ecowitt-tile-label-btn"
          >
            Label channels →
          </button>
        </span>
      </div>

      <span className="hidden">{tick}</span>
    </div>
  );
};
